import { useEffect, useState } from 'react';

import { ProductType, Quote } from '@generated/gql/graphql';
import { useUpdatedTariffModalContext } from '@providers/basket/UpdatedTariffModalProvider/Context';
import { OutdatedBundleModalVariant } from '@src/types/bundle';

import { categorizeQuotes, hasExpiredQuotes } from './categorizeQuotes';
import { getEnergyModalVariant, getModalCharacteristics } from './modals';
import { prepareProductIds } from './preparePayload';
import { IdsState, ModalState } from './types';
import { useSelectBundle } from './useSelectBundle';

const useOutdatedBundleModal = (quotes: Quote[], refetchBasket: () => void) => {
  const [modalState, setModalState] = useState<ModalState>({
    variant: OutdatedBundleModalVariant.none,
    removedProducts: null,
  });
  const [ids, setIds] = useState<Nullable<IdsState>>(null);
  const { setModalState: setEnergyModalState } = useUpdatedTariffModalContext();
  const { handleSelection, isLoading, error } = useSelectBundle();

  useEffect(() => {
    if (!quotes.length) {
      return;
    }

    const categorizedQuotes = categorizeQuotes(quotes);

    if (!hasExpiredQuotes(categorizedQuotes)) {
      return;
    }

    const preparedIds = prepareProductIds(categorizedQuotes);
    setIds(preparedIds);

    const modalCharacteristics = getModalCharacteristics(categorizedQuotes);
    const energyModalState = getEnergyModalVariant(
      categorizedQuotes[ProductType.Energy] ?? [],
      modalCharacteristics.variant
    );

    setModalState(modalCharacteristics);

    if (energyModalState) {
      setEnergyModalState(energyModalState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes]);

  const resetState = () => {
    setIds(null);
    setModalState({
      variant: OutdatedBundleModalVariant.none,
      removedProducts: null,
    });
  };

  const handleSelectionSuccess = () => {
    refetchBasket();
    resetState();
  };

  const handleOnContinue = () => {
    if (!ids) {
      return;
    }

    return handleSelection(ids, handleSelectionSuccess, resetState);
  };

  return {
    modalState,
    ids,
    isLoading,
    error,
    handleOnContinue,
  };
};

export { useOutdatedBundleModal };
